/**
 * Production adapter for `reset_display`: the SAME calls as the viewer's
 * reset buttons —
 *  - view: `ResetViewTool` → `viewManager.resetViews()`;
 *  - window-level: `WindowingControls` Reset → `WindowLevelSetEvent` with
 *    the image's default (acquisition, else auto, else default window),
 *    for the primary and, when loaded, the second image;
 *  - contour-thickness: `GlobalContourThicknessModifier` Reset → the global
 *    thickness atom + `StructureSetContourThicknessChangeRequestedEvent`;
 *  - dose-opacity: `DoseOpacityModifier` Reset → the opacity atom +
 *    `environment.updateDoseAlphaLevelOpacity`;
 *  - layout: `ResizeLayoutModifier` Reset → `defaultMainViewWidth`.
 * Browser glue covered by the manual demo; the logic is unit-tested
 * through fakes.
 */
import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    domainEventBus,
    StructureSetContourThicknessChangeRequestedEvent,
    WindowLevelSetEvent,
} from '~/domain/events';
import type { Image3D } from '~/domain/patient/image3d';
import { DEFAULT_DOSE_OPACITY_VALUE } from '~/engine/data/dose/DoseDistribution';
import {
    currentDoseState,
    doseOpacityState,
} from '~/state/atoms/dose/doseAtoms';
import { environmentState, viewManagerState } from '~/state/atoms/engineAtoms';
import {
    primaryImageState,
    secondaryImageState,
} from '~/state/atoms/image/imageAtoms';
import {
    defaultGlobalROIContourThickness,
    globalROIContourThicknessState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    defaultMainViewWidth,
    mainViewWidthState,
} from '~/state/atoms/toolsAtoms';
import type { ResetDisplayDeps } from './resetDisplay';

/** The overlay Reset's default window for an image. */
const defaultWindowLevel = (image: Image3D) =>
    image.getAcquisitionWindowLevel() ??
    image.getAutoWindowLevel() ??
    image.getDefaultWindowLevel();

export function useResetDisplayDeps(): () => ResetDisplayDeps {
    const env = useRecoilValue(environmentState);
    const viewManager = useRecoilValue(viewManagerState);
    const primaryImage = useRecoilValue(primaryImageState);
    const secondaryImage = useRecoilValue(secondaryImageState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const dose = useRecoilValue(currentDoseState);
    const setThickness = useSetRecoilState(globalROIContourThicknessState);
    const setDoseOpacity = useSetRecoilState(doseOpacityState);
    const setMainViewWidth = useSetRecoilState(mainViewWidthState);

    return useCallback(
        (): ResetDisplayDeps => ({
            view: () => {
                if (!viewManager || !primaryImage) {
                    return 'no image is loaded';
                }
                viewManager.resetViews();
                return undefined;
            },
            'window-level': () => {
                const images = [primaryImage, secondaryImage].filter(
                    (i): i is Image3D => !!i
                );
                if (images.length === 0) {
                    return 'no image is loaded';
                }
                for (const image of images) {
                    domainEventBus.publishSync(
                        new WindowLevelSetEvent(
                            defaultWindowLevel(image),
                            image.seriesUid,
                            'user'
                        )
                    );
                }
                return undefined;
            },
            'contour-thickness': () => {
                setThickness(defaultGlobalROIContourThickness);
                if (structureSet?.dicomUid) {
                    void domainEventBus.publish(
                        new StructureSetContourThicknessChangeRequestedEvent(
                            structureSet.dicomUid,
                            defaultGlobalROIContourThickness
                        )
                    );
                }
                return undefined;
            },
            'dose-opacity': () => {
                if (!dose) {
                    return 'no dose is loaded';
                }
                setDoseOpacity(DEFAULT_DOSE_OPACITY_VALUE);
                env?.updateDoseAlphaLevelOpacity(DEFAULT_DOSE_OPACITY_VALUE);
                return undefined;
            },
            layout: () => {
                setMainViewWidth(defaultMainViewWidth);
                return undefined;
            },
        }),
        [
            env,
            viewManager,
            primaryImage,
            secondaryImage,
            structureSet,
            dose,
            setThickness,
            setDoseOpacity,
            setMainViewWidth,
        ]
    );
}
